import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { BACKEND_URL } from '../config/canonical';
import {
  FileSignature, Search, Filter, RefreshCw, Eye, Send, Download, Trash2,
  XCircle, Clock, CheckCircle, AlertTriangle, ChevronDown, Calendar,
  User, Briefcase, FileText, RotateCcw, Pencil,
} from 'lucide-react';
import CaseAcceptanceEditModal from '../components/case/CaseAcceptanceEditModal';

const STATUS_BADGES = {
  PENDING_ACCEPTANCE: { label: 'Pending', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  VIEWED: { label: 'Viewed', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  SIGNED: { label: 'Signed', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  REJECTED: { label: 'Rejected', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  EXPIRED: { label: 'Expired', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  VOIDED: { label: 'Voided', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  DELETED: { label: 'Deleted', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
};

const CaseAcceptance = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedForm, setSelectedForm] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // list, detail
  const [editingForm, setEditingForm] = useState(null);
  const token = localStorage.getItem('nexus_token');

  const isAdmin = ['main_hub', 'department_admin'].includes(user?.role);

  const fetchForms = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/case-acceptance/forms`;
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setForms(data);
      }
    } catch (e) {
      console.error('Failed to fetch forms:', e);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  const fetchStats = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/case-acceptance/dashboard-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setStats(await res.json());
    } catch (e) {
      console.error('Failed to fetch stats:', e);
    }
  }, [token, isAdmin]);

  useEffect(() => {
    fetchForms();
    fetchStats();
  }, [fetchForms, fetchStats]);

  const handleResend = async (formId) => {
    if (!window.confirm('Resend notification to the investigator?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/case-acceptance/forms/${formId}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchForms();
        alert('Notification resent successfully');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleVoid = async (formId) => {
    if (!window.confirm('Void this form? This action cannot be undone.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/case-acceptance/forms/${formId}/void`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchForms();
        setSelectedForm(null);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (formId) => {
    if (!window.confirm('Delete this form? This action cannot be undone.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/case-acceptance/forms/${formId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchForms();
        setSelectedForm(null);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const filteredForms = forms.filter(form => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      form.case_number?.toLowerCase().includes(q) ||
      form.investigator_full_name?.toLowerCase().includes(q) ||
      form.investigator_username?.toLowerCase().includes(q) ||
      form.id?.toLowerCase().includes(q)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-6" data-testid="case-acceptance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileSignature className="w-7 h-7 text-amber-500" />
            Case Acceptance
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {isAdmin ? 'Manage case assignment acceptance forms' : 'Your pending case acceptance forms'}
          </p>
        </div>
        <Button onClick={fetchForms} variant="outline" className="border-slate-600" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Admin Stats */}
      {isAdmin && stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <Card className="bg-amber-500/10 border-amber-500/30">
            <CardContent className="p-3 text-center">
              <Clock className="w-5 h-5 text-amber-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-amber-400">{stats.pending || 0}</div>
              <div className="text-xs text-amber-300">Pending</div>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-3 text-center">
              <Eye className="w-5 h-5 text-blue-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-blue-400">{stats.viewed || 0}</div>
              <div className="text-xs text-blue-300">Viewed</div>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/10 border-emerald-500/30">
            <CardContent className="p-3 text-center">
              <CheckCircle className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-emerald-400">{stats.signed || 0}</div>
              <div className="text-xs text-emerald-300">Signed</div>
            </CardContent>
          </Card>
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="p-3 text-center">
              <XCircle className="w-5 h-5 text-red-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-red-400">{stats.rejected || 0}</div>
              <div className="text-xs text-red-300">Rejected</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-500/10 border-slate-500/30">
            <CardContent className="p-3 text-center">
              <AlertTriangle className="w-5 h-5 text-slate-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-slate-400">{stats.expired || 0}</div>
              <div className="text-xs text-slate-300">Expired</div>
            </CardContent>
          </Card>
          <Card className="bg-purple-500/10 border-purple-500/30">
            <CardContent className="p-3 text-center">
              <FileSignature className="w-5 h-5 text-purple-400 mx-auto mb-1" />
              <div className="text-2xl font-bold text-purple-400">{stats.total || 0}</div>
              <div className="text-xs text-purple-300">Total</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-slate-900/50 border-slate-700 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by case number, investigator..."
                className="pl-10 bg-slate-800 border-slate-600"
                data-testid="search-input"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-md text-white"
              data-testid="status-filter"
            >
              <option value="">All Statuses</option>
              <option value="PENDING_ACCEPTANCE">Pending</option>
              <option value="VIEWED">Viewed</option>
              <option value="SIGNED">Signed</option>
              <option value="REJECTED">Rejected</option>
              <option value="EXPIRED">Expired</option>
              <option value="VOIDED">Voided</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Forms Table */}
      <Card className="bg-slate-900/50 border-slate-700">
        <CardHeader className="border-b border-slate-700">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-500" />
            {isAdmin ? 'All Submissions' : 'My Acceptance Forms'}
            <Badge variant="secondary" className="ml-2">{filteredForms.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading forms...</div>
          ) : filteredForms.length === 0 ? (
            <div className="text-center py-12">
              <FileSignature className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No acceptance forms found</p>
              <p className="text-slate-500 text-sm">Forms are auto-generated when cases are assigned</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr className="text-left text-slate-400 text-xs uppercase">
                    <th className="px-4 py-3">Form ID</th>
                    <th className="px-4 py-3">Case</th>
                    <th className="px-4 py-3 hidden md:table-cell">Investigator</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Assigned</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 hidden sm:table-cell">Expires</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {filteredForms.map(form => (
                    <tr key={form.id} className="hover:bg-slate-800/30 transition-colors" data-testid={`form-row-${form.id}`}>
                      <td className="px-4 py-3">
                        <span className="font-mono text-amber-400 text-sm">{form.id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-white font-medium">{form.case_number || '-'}</div>
                        <div className="text-slate-400 text-xs truncate max-w-[150px]">{form.case_title}</div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="text-white">{form.investigator_full_name}</div>
                        <div className="text-slate-400 text-xs">@{form.investigator_username}</div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-300 text-sm">
                        {formatDate(form.date_assigned)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={STATUS_BADGES[form.status]?.color || 'bg-slate-600'}>
                          {STATUS_BADGES[form.status]?.label || form.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-slate-400 text-sm">
                        {form.status === 'SIGNED' ? (
                          <span className="text-emerald-400">Completed</span>
                        ) : form.expires_at ? (
                          formatDate(form.expires_at)
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {/* Sign button: primary action for the assigned user when pending */}
                          {user?.id === form.assigned_user_id && ['PENDING_ACCEPTANCE', 'VIEWED'].includes(form.status) && (
                            <Button
                              size="sm"
                              onClick={() => navigate(`/case-acceptance/${form.id}`)}
                              className="bg-emerald-600 hover:bg-emerald-500 text-white h-7 px-3"
                              data-testid={`sign-btn-${form.id}`}
                              title="Open and sign this form"
                            >
                              <FileSignature className="w-3.5 h-3.5 mr-1" /> Sign
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if (user?.id === form.assigned_user_id && ['PENDING_ACCEPTANCE', 'VIEWED'].includes(form.status)) {
                                navigate(`/case-acceptance/${form.id}`);
                              } else {
                                setSelectedForm(form);
                              }
                            }}
                            className="text-blue-400 hover:text-blue-300"
                            data-testid={`view-btn-${form.id}`}
                            title={user?.id === form.assigned_user_id && ['PENDING_ACCEPTANCE', 'VIEWED'].includes(form.status) ? 'Open to sign' : 'View form'}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {isAdmin && ['PENDING_ACCEPTANCE', 'VIEWED', 'EXPIRED'].includes(form.status) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingForm(form)}
                              className="text-emerald-400 hover:text-emerald-300"
                              title="Edit assignment fields before sending"
                              data-testid={`edit-btn-${form.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          {isAdmin && ['PENDING_ACCEPTANCE', 'VIEWED', 'EXPIRED'].includes(form.status) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleResend(form.id)}
                              className="text-amber-400 hover:text-amber-300"
                              title="Resend Notification"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          {isAdmin && !['SIGNED', 'DELETED'].includes(form.status) && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleVoid(form.id)}
                                className="text-purple-400 hover:text-purple-300"
                                title="Void Form"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(form.id)}
                                className="text-red-400 hover:text-red-300"
                                title="Delete Form"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Detail Modal */}
      {selectedForm && (
        <FormDetailModal
          form={selectedForm}
          isAdmin={isAdmin}
          onClose={() => setSelectedForm(null)}
          onRefresh={fetchForms}
          token={token}
        />
      )}
      {editingForm && (
        <CaseAcceptanceEditModal
          form={editingForm}
          open={!!editingForm}
          onOpenChange={(o) => { if (!o) setEditingForm(null); }}
          onSaved={fetchForms}
        />
      )}
    </div>
  );
};

// Form Detail Modal Component
const FormDetailModal = ({ form, isAdmin, onClose, onRefresh, token }) => {
  const [auditLog, setAuditLog] = useState([]);
  const [signature, setSignature] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      // Fetch audit log for admins
      if (isAdmin) {
        try {
          const res = await fetch(`${BACKEND_URL}/api/case-acceptance/forms/${form.id}/audit-log`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) setAuditLog(await res.json());
        } catch (e) { console.error(e); }
      }

      // Fetch signature if signed
      if (form.status === 'SIGNED') {
        try {
          const res = await fetch(`${BACKEND_URL}/api/case-acceptance/forms/${form.id}/signature`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) setSignature(await res.json());
        } catch (e) { console.error(e); }
      }
    };
    fetchDetails();
  }, [form, isAdmin, token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 overflow-y-auto py-4 px-3">
      <div className="bg-slate-900 rounded-lg border border-slate-700 w-full max-w-4xl my-auto overflow-hidden" data-testid="form-detail-modal" style={{ minWidth: 0 }}>
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-4 border-b border-slate-700 min-w-0">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-white truncate">Case Acceptance Form</h3>
            <p className="text-sm text-amber-400 font-mono truncate">{form.id}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge className={STATUS_BADGES[form.status]?.color}>
              {STATUS_BADGES[form.status]?.label || form.status}
            </Badge>
            <button onClick={onClose} className="text-slate-400 hover:text-white" aria-label="Close">
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 max-h-[70vh] overflow-y-auto space-y-6">
          {/* Assignment Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-amber-400 uppercase tracking-wide flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Assignment Information
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Case Number:</span><span className="text-white font-mono break-all text-right min-w-0">{form.case_number || '-'}</span></div>
                {/* MASTER DIRECTIVE #28 — parity with PDF */}
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Case Type:</span><span className="text-white break-words text-right min-w-0">{form.case_type || '-'}</span></div>
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Client:</span><span className="text-white break-words text-right min-w-0">{form.client_name || '-'}</span></div>
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Client Email:</span><span className="text-white break-words text-right min-w-0" data-testid="ca-admin-client-email">{form.client_email || '-'}</span></div>
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Client Phone:</span><span className="text-white break-words text-right min-w-0" data-testid="ca-admin-client-phone">{form.client_phone || '-'}</span></div>
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Assigned Investigator:</span><span className="text-white break-words text-right min-w-0" data-testid="ca-admin-investigator">{form.investigator_full_name || '-'}</span></div>
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Assignment Type:</span><span className="text-white break-words text-right min-w-0">{form.assignment_type || '-'}</span></div>
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Start Date:</span><span className="text-white text-right min-w-0">{formatDate(form.start_date)}</span></div>
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">End Date:</span><span className="text-white text-right min-w-0">{formatDate(form.end_date)}</span></div>
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Due Date:</span><span className="text-white text-right min-w-0" data-testid="ca-admin-due-date">{formatDate(form.case_due_date) || '-'}</span></div>
                {(form.case_description || form.case_summary) && (
                  <div className="col-span-full pt-1 min-w-0">
                    <span className="text-slate-400 shrink-0">Case Description:</span>
                    <p className="text-white text-sm mt-1 break-words" data-testid="ca-admin-case-description">{form.case_description || form.case_summary}</p>
                  </div>
                )}
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Case Title:</span><span className="text-white break-words text-right min-w-0">{form.case_title || '-'}</span></div>
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Priority:</span><span className="text-white text-right min-w-0">{form.priority_level || 'Normal'}</span></div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-blue-400 uppercase tracking-wide flex items-center gap-2">
                <User className="w-4 h-4" /> Investigator Information
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Full Name:</span><span className="text-white break-words text-right min-w-0">{form.investigator_full_name || '-'}</span></div>
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Username:</span><span className="text-white font-mono break-all text-right min-w-0">@{form.investigator_username || '-'}</span></div>
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">License #:</span><span className="text-white break-all text-right min-w-0">{form.investigator_license_number || '-'}</span></div>
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Phone:</span><span className="text-white text-right min-w-0">{form.investigator_phone || '-'}</span></div>
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Email:</span><span className="text-white break-all text-right min-w-0">{form.investigator_email || '-'}</span></div>
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 min-w-0"><span className="text-slate-400 shrink-0">Role:</span><span className="text-white capitalize text-right min-w-0">{form.investigator_role?.replace('_', ' ') || '-'}</span></div>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="bg-slate-800/50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Timeline
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-slate-400 block">Generated</span>
                <span className="text-white">{formatDate(form.generated_at)}</span>
              </div>
              <div>
                <span className="text-slate-400 block">Sent</span>
                <span className="text-white">{formatDate(form.sent_at)}</span>
              </div>
              <div>
                <span className="text-slate-400 block">Viewed</span>
                <span className={form.viewed_at ? 'text-blue-400' : 'text-slate-500'}>{formatDate(form.viewed_at)}</span>
              </div>
              <div>
                <span className="text-slate-400 block">Signed</span>
                <span className={form.signed_at ? 'text-emerald-400' : 'text-slate-500'}>{formatDate(form.signed_at)}</span>
              </div>
            </div>
            {form.expires_at && form.status !== 'SIGNED' && (
              <div className="mt-3 pt-3 border-t border-slate-700">
                <span className="text-slate-400">Expires:</span>
                <span className="text-amber-400 ml-2">{formatDate(form.expires_at)}</span>
              </div>
            )}
          </div>

          {/* Rejection Reason */}
          {form.status === 'REJECTED' && form.rejection_reason && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
              <h4 className="text-red-400 font-semibold mb-2">Rejection Reason</h4>
              <p className="text-white">{form.rejection_reason}</p>
            </div>
          )}

          {/* Signature Display */}
          {signature && (
            <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-4">
              <h4 className="text-emerald-400 font-semibold mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Electronic Signature
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-slate-400 text-sm">Typed Name:</span>
                  <p className="text-white font-semibold">{signature.typed_name}</p>
                </div>
                <div>
                  <span className="text-slate-400 text-sm">Signed At:</span>
                  <p className="text-white">{formatDate(signature.signed_at)}</p>
                </div>
              </div>
              {signature.signature_data && (
                <div className="mt-3">
                  <span className="text-slate-400 text-sm">Signature:</span>
                  <div className="bg-white rounded-lg p-2 mt-1 inline-block">
                    <img src={signature.signature_data} alt="Signature" className="max-h-24" />
                  </div>
                </div>
              )}
              {isAdmin && (
                <div className="mt-3 pt-3 border-t border-emerald-500/30 text-xs text-slate-400">
                  <span>IP: {signature.ip_address || 'N/A'}</span>
                  <span className="mx-2">|</span>
                  <span>Device: {signature.device_info?.substring(0, 50) || 'N/A'}</span>
                </div>
              )}
            </div>
          )}

          {/* Audit Log */}
          {isAdmin && auditLog.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-300 mb-3">Audit Log</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {auditLog.map(log => (
                  <div key={log.id} className="flex items-start gap-3 text-sm bg-slate-800/30 rounded p-2">
                    <span className="text-slate-500 whitespace-nowrap">{formatDate(log.created_at)}</span>
                    <span className="text-slate-300">{log.action_description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Special Instructions */}
          {form.special_instructions && (
            <div className="bg-slate-800/50 rounded-lg p-4">
              <h4 className="text-slate-300 font-semibold mb-2">Special Instructions</h4>
              <p className="text-white whitespace-pre-wrap">{form.special_instructions}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-4 border-t border-slate-700 flex flex-wrap justify-end gap-2 min-w-0">
          <a
            href={`${BACKEND_URL}/api/case-acceptance/forms/${form.id}/export-pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex"
          >
            <Button variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10">
              <Download className="w-4 h-4 mr-2" /> Export PDF
            </Button>
          </a>
          <Button variant="outline" onClick={onClose} className="border-slate-600">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CaseAcceptance;
